import { useState, useSyncExternalStore, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { useSearch } from '../../hooks/use-search';
import { Icon } from '../icons/icon';
import { FeedSkeleton, Notice, StateMessage, useSavedAgo } from './discovery-states';
import { RankingDialog, type RankingDialogState } from './ranking-dialog';
import { ResultsList } from './results-list';

type SearchState = ReturnType<typeof useSearch>;

export interface SearchFeedProps {
  search: SearchState;
  hasOrigin: boolean;
  /** Origem ainda a resolver (sessão/perfil): skeleton, nunca um «escolha a cidade» precipitado. */
  locationPending: boolean;
  label: string;
  /** `filters`: há filtros/termo a retirar. `zone`: a zona é que não tem oferta. */
  emptyKind: 'zone' | 'filters';
  onChooseCity: () => void;
  onClearFilters?: () => void;
  /** Só existe quando alargar muda alguma coisa (há origem e raio < 50 km). */
  onWiden?: () => void;
  /** Aberto de fora (link «Como ordenamos» do cabeçalho da secção). */
  howOpen?: boolean;
  onHowClose?: () => void;
}

function subscribeOnline(onChange: () => void) {
  window.addEventListener('online', onChange);
  window.addEventListener('offline', onChange);
  return () => {
    window.removeEventListener('online', onChange);
    window.removeEventListener('offline', onChange);
  };
}

/**
 * Resultado da busca com todos os estados da C7, na ordem em que a pessoa pode agir:
 * localização em falta → a carregar → offline (snapshot público) → 429 → erro → vazio → lista.
 *
 * Offline mostra até 20 negócios PÚBLICOS do último feed, com a idade visível; o snapshot nunca
 * tem favoritos, distância, termo ou coordenadas (B4). Um erro depois da 1.ª página mantém a
 * lista carregada e oferece repetir só o que faltou.
 */
export function SearchFeed({
  search,
  hasOrigin,
  locationPending,
  label,
  emptyKind,
  onChooseCity,
  onClearFilters,
  onWiden,
  howOpen = false,
  onHowClose,
}: SearchFeedProps) {
  const { t } = useTranslation();
  const online = useSyncExternalStore(subscribeOnline, () => navigator.onLine, () => true);
  const [dialog, setDialog] = useState<RankingDialogState>(null);
  const snapshot = online ? null : search.offlineSnapshot;
  const savedAgo = useSavedAgo(snapshot?.savedAt);
  const dialogState: RankingDialogState = dialog ?? (howOpen ? { mode: 'how' } : null);

  const rankingDialog = (
    <RankingDialog
      state={dialogState}
      weights={search.weights}
      onClose={() => {
        setDialog(null);
        onHowClose?.();
      }}
      onShowHow={() => setDialog({ mode: 'how' })}
    />
  );

  // O diálogo acompanha todos os estados: «Como ordenamos» fica disponível mesmo sem resultados.
  const withDialog = (node: ReactNode) => (
    <>
      {node}
      {rankingDialog}
    </>
  );

  if (locationPending) return withDialog(<FeedSkeleton />);

  if (search.needsLocation) {
    return withDialog(
      <StateMessage
        icon="pin"
        title={t('discovery.states.needsLocation.title')}
        text={t('discovery.states.needsLocation.text')}
        testId="state-needs-location"
        actions={
          <button className="btn btn-secondary" type="button" aria-haspopup="dialog" onClick={onChooseCity}>
            {t('discovery.location.choose')}
          </button>
        }
      />
    );
  }

  if (search.results.length === 0 && !online) {
    if (snapshot && snapshot.businesses.length > 0) {
      return withDialog(
        <div className="pc-sec gap-4" data-testid="state-offline">
          <Notice tone="info" icon="wifiOff">
            {t('discovery.states.offline.cached')} {savedAgo ? t('discovery.states.offline.savedAgo', { ago: savedAgo }) : null}
          </Notice>
          <ResultsList offline={snapshot.businesses} hasOrigin={false} hasMore={false} isFetchingMore={false} onLoadMore={() => undefined} label={label} />
        </div>
      );
    }
    return withDialog(
      <StateMessage
        icon="wifiOff"
        title={t('discovery.states.offline.title')}
        text={t('discovery.states.offline.text')}
        testId="state-offline-empty"
        actions={
          <button className="btn btn-secondary" type="button" onClick={() => void search.refetch()}>
            <Icon name="refresh" size={18} />
            {t('actions.retry')}
          </button>
        }
      />
    );
  }

  if (search.isLoading) return withDialog(<FeedSkeleton />);

  const retry = (
    <button className="btn btn-secondary" type="button" onClick={() => void search.refetch()}>
      <Icon name="refresh" size={18} />
      {t('actions.retry')}
    </button>
  );

  if (search.isError && search.results.length === 0) {
    if (search.errorCode === 'rate_limited') {
      return withDialog(
        <StateMessage
          icon="clock"
          title={t('discovery.states.rateLimited.title')}
          text={
            search.retryAfterSeconds
              ? t('discovery.states.rateLimited.textSeconds', { count: search.retryAfterSeconds })
              : t('discovery.states.rateLimited.text')
          }
          testId="state-rate-limited"
          actions={retry}
        />
      );
    }
    if (search.errorCode === 'invalid_request' && onClearFilters) {
      return withDialog(
        <StateMessage
          icon="sliders"
          title={t('discovery.states.invalid.title')}
          text={t('discovery.states.invalid.text')}
          testId="state-invalid"
          actions={<button className="btn btn-secondary" type="button" onClick={onClearFilters}>{t('discovery.filters.clearAll')}</button>}
        />
      );
    }
    return withDialog(
      <StateMessage
        icon="alert"
        title={t('discovery.states.error.title')}
        text={t('discovery.states.error.text')}
        testId="state-error"
        actions={retry}
      />
    );
  }

  if (search.results.length === 0) {
    const filters = emptyKind === 'filters';
    return withDialog(
      <StateMessage
        icon={filters ? 'sliders' : 'pin'}
        title={t(filters ? 'discovery.states.emptyFilters.title' : 'discovery.states.emptyZone.title')}
        text={t(filters ? 'discovery.states.emptyFilters.text' : 'discovery.states.emptyZone.text')}
        testId={filters ? 'state-empty-filters' : 'state-empty-zone'}
        actions={
          <>
            {filters && onClearFilters ? (
              <button className="btn btn-secondary" type="button" onClick={onClearFilters}>{t('discovery.filters.clearAll')}</button>
            ) : null}
            {onWiden ? (
              <button className={filters ? 'hit pc-textbtn' : 'btn btn-secondary'} type="button" onClick={onWiden}>
                {t('discovery.states.widen')}
              </button>
            ) : null}
            <button className="hit pc-textbtn" type="button" aria-haspopup="dialog" onClick={onChooseCity}>
              {t('discovery.states.changeCity')}
            </button>
          </>
        }
      />
    );
  }

  return withDialog(
    <>
      <ResultsList
        results={search.results}
        hasOrigin={hasOrigin}
        hasMore={search.hasMore}
        isFetchingMore={search.isFetchingMore}
        onLoadMore={() => void search.fetchMore()}
        onExplain={(result) => setDialog({ mode: 'why', result })}
        label={label}
      />
      {search.isError ? (
        <Notice
          tone="warn"
          icon="alert"
          testId="state-more-error"
          action={
            <button className="btn btn-ghost btn-sm hit" type="button" onClick={() => void search.fetchMore()}>
              {t('actions.retry')}
            </button>
          }
        >
          {search.errorCode === 'rate_limited' ? t('discovery.states.rateLimited.text') : t('discovery.states.moreError')}
        </Notice>
      ) : null}
    </>,
  );
}
